import {Button, Container, Group, NumberInput, Stack, Stepper, Text, TextInput, Title} from '@mantine/core';
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {CustomerSelector} from '../components/CustomerSelector';
import {FlowResultDisplay} from '../components/FlowResultDisplay';
import {FlowResult, extractErrorMessage} from '../types';
import {add as createInvoice, send as sendInvoice} from '../../invoices/services/InvoicesService';
import {add as createInvoiceItem} from '../../invoice-items/services/InvoiceItemsService';
import {Invoice} from '../../invoices/types';
import {notifyError, notifySuccess} from '../../../components/ServiceNotifications';

export const InvoicePaymentPage = () => {
    const navigate = useNavigate();
    const [active, setActive] = useState(0);
    const [customerId, setCustomerId] = useState<string | null>(null);
    const [description, setDescription] = useState('Payment Flow Tester - invoice item');
    const [unitAmount, setUnitAmount] = useState<number>(5000);
    const [quantity, setQuantity] = useState<number>(1);
    const [processing, setProcessing] = useState(false);
    const [result, setResult] = useState<FlowResult | null>(null);

    const handleCreateAndSend = async () => {
        if (!customerId) return;
        setProcessing(true);
        try {
            // Step 1: Create invoice
            const invoice = await createInvoice({
                customerId,
                collectionMethod: 'sendInvoice',
                daysUntilDue: 30,
                description: 'Payment Flow Tester - test invoice',
            }) as Invoice;
            console.log('Invoice created:', invoice);

            // Step 2: Add invoice item
            const item = await createInvoiceItem({
                customerId,
                invoice: invoice.id,
                description,
                amount: unitAmount * quantity,
                currency: 'usd',
            });
            console.log('Invoice item created:', item);

            // Step 3: Send invoice
            const sent = await sendInvoice(invoice.id) as Invoice;
            console.log('Invoice sent:', sent);
            notifySuccess('Invoice created and sent');

            setResult({
                success: true,
                intentId: invoice.id,
                status: sent?.status || 'sent',
            });
            setActive(1);
        } catch (e) {
            notifyError(e);
            setResult({success: false, error: extractErrorMessage(e)});
            setActive(1);
        } finally {
            setProcessing(false);
        }
    };

    const handleReset = () => {
        setActive(0);
        setCustomerId(null);
        setDescription('Payment Flow Tester - invoice item');
        setUnitAmount(5000);
        setQuantity(1);
        setResult(null);
    };

    return (
        <Container size="sm" py="xl">
            <Stack gap="xl">
                <div>
                    <Title order={3}>Invoice Payment</Title>
                    <Text c="dimmed" size="sm">
                        Create an invoice, add a line item, and send it to the customer.
                    </Text>
                </div>

                <Stepper active={active} allowNextStepsSelect={false}>
                    <Stepper.Step label="Invoice Details"/>
                    <Stepper.Step label="Result"/>
                </Stepper>

                {active === 0 && (
                    <Stack gap="md">
                        <CustomerSelector value={customerId} onChange={setCustomerId}/>
                        <TextInput
                            label="Line Item Description"
                            value={description}
                            onChange={e => setDescription(e.currentTarget.value)}
                        />
                        <NumberInput
                            label="Unit Amount (in cents)"
                            placeholder="e.g. 5000 = $50.00"
                            value={unitAmount}
                            onChange={v => setUnitAmount(Number(v))}
                            min={1}
                        />
                        <NumberInput
                            label="Quantity"
                            value={quantity}
                            onChange={v => setQuantity(Number(v) || 1)}
                            min={1}
                        />
                        <Group justify="space-between">
                            <Button variant="subtle" onClick={() => navigate('/payment-flow-tester')}>Back</Button>
                            <Button
                                onClick={handleCreateAndSend}
                                disabled={!customerId || !unitAmount}
                                loading={processing}
                            >
                                Create & Send Invoice
                            </Button>
                        </Group>
                    </Stack>
                )}

                {active === 1 && result && (
                    <FlowResultDisplay result={result} onReset={handleReset}/>
                )}
            </Stack>
        </Container>
    );
};
